/**
 * @fileOverview The machines walkthrough. Pure data.
 * See docs/view-help.md and docs/view-help/machines.md.
 */

import { ENTITY_ICONS, ITEM_KIND_ICONS } from "@/services/entityIcons"
import { PERMISSIONS } from "@/services/permission"
import { hasPermissionsOfAll } from "@/services/permissionService"
import { isSupplierStrict } from "@/services/supplierMode"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpTour } from "@/services/viewHelp/types"

/** Reading the machine catalogue — the list and a machine's page need it (`router/routes/machine.js`). */
const VIEW = [PERMISSIONS.VIEW_MACHINE_GENERAL]

/** Entering or changing one — the create button and Speichern. */
const EDIT = [PERMISSIONS.EDIT_MACHINE_GENERAL]

/** The purchase price is a right of its own: without it the column and the Einkauf fields are gone. */
const VIEW_PRICE = [PERMISSIONS.VIEW_MACHINE_PURCHASEPRICE]

/**
 * Once the workspace's setup is done, the prices are suppliers' and live in an Einkauf tab of their own; before,
 * they are a group of the Allgemein tab (docs/suppliers.md, "Setup and strict mode"). Each state has its steps.
 */
const strict = () => isSupplierStrict()
const legacy = () => !isSupplierStrict()

/** "Preis hinzufügen": changing the machine's prices, and picking a supplier, which needs seeing them. */
const mayAddSupplier = () =>
  isSupplierStrict() &&
  hasPermissionsOfAll([PERMISSIONS.EDIT_MACHINE_PURCHASEPRICE, PERMISSIONS.VIEW_SUPPLIER_GENERAL]).value

/** The hint under the suppliers counts bookings only for somebody who may re-price a project's machines. */
const REPRICE = [
  PERMISSIONS.EDIT_ASSIGNED_PROJECT_MACHINE_PURCHASEPRICE,
  PERMISSIONS.EDIT_FOREIGN_PROJECT_MACHINE_PURCHASEPRICE,
]

/** The sample machine every step of its page is read on — the world's Minibagger 3 t. */
const MACHINE = { machineId: "mach-bagger" }

const onOverview = stepsOn("MachineOverview", { permissions: VIEW })
const onDetail = stepsOn("MachineDetail", { permissions: VIEW, at: { params: MACHINE } })
// Two machines side by side (the add-on's Vergleich, a machine's "Aktionen → Vergleichen"): the Kipper and the
// Minibagger, both rented from Brandl and Mietpark Nord; the Kipper is also the workspace's own.
const onComparison = stepsOn("MachineComparison", {
  navEntry: "nav-machine-comparison",
  at: { query: { ids: "mach-kipper,mach-bagger" } },
})

// The related views: where a machine takes effect outside the catalogue — the customer's own selling
// price, the packet that bundles it, the project registers that carry and book it, and the running
// post-calculation that prices what was booked. The pointer walks there (`services/viewHelp/ways.ts`); `at` says
// where to load the frame if that walk ever fails.
const onCustomer = stepsOn("CustomerDetail", {
  permissions: [PERMISSIONS.VIEW_CUSTOMER_MACHINE],
  at: { params: { customerId: "c-beispiel" }, hash: "machines" },
})
const onPacket = stepsOn("PacketDetail", {
  permissions: [PERMISSIONS.VIEW_PACKET_GENERAL],
  at: { params: { packetId: "pkt-erdbau" } },
})
const onProject = stepsOn("ProjectDetail", {
  permissions: [PERMISSIONS.VIEW_ASSIGNED_PROJECT_MACHINE, PERMISSIONS.VIEW_FOREIGN_PROJECT_MACHINE],
  at: { params: { projectId: "p-nord" }, hash: "machines" },
})
// The Stundenlohnarbeiten register of the same project — the register a machine is actually booked
// in, gated by its own pair of rights rather than by the Maschinen one.
const onProjectReports = stepsOn("ProjectDetail", {
  permissions: [PERMISSIONS.VIEW_ASSIGNED_PROJECT_HOURLYWAGEREPORT, PERMISSIONS.VIEW_FOREIGN_PROJECT_HOURLYWAGEREPORT],
  at: { params: { projectId: "p-nord" }, hash: "hourlyWageReports" },
})
// And where those booked hours are priced. `nav-costs` is the drawer entry's own gate, the pair of
// cost rights the four cost routes are guarded by — one place decides who sees any of them.
const onCosts = stepsOn("ProjectCostsForOne", {
  navEntry: "nav-costs",
  at: { params: { projectId: "p-nord" } },
})

/**
 * Machines: the fleet every project, packet and customer price list draws from, and the place the
 * hourly purchase price each of them calculates with is kept.
 *
 * The list first; then the Minibagger's page, whose registers the pointer walks through — the master data, the
 * purchase price (a group of Allgemein before the supplier setup, the Einkauf tab with its suppliers after
 * it), the note, the Dateiablage and the Protokoll — with Speichern
 * pointed at and never pressed. The Zusammenhänge chapter is five steps on the places outside the
 * catalogue where a machine shows up, each naming what it does there — the Stundenlohnarbeit opened
 * on the Maschinen register it is booked in.
 *
 * The twin of the materials tour (`tours/materials.ts`): the two features share their views, their
 * components and their wording, and a change to one of them nearly always belongs in the other. What
 * is genuinely different is the price, which is per unit of use rather than per quantity built in —
 * see the `project-machines` step.
 */
export const MACHINES_TOUR: ViewHelpTour = {
  key: "machines",
  icon: ENTITY_ICONS.machine,
  // The create form has no steps of its own; its trigger opens the tour at the top of the list.
  routes: ["MachineOverview", "MachineDetail", "MachineCreate", "MachineComparison"],
  // Where a machine shows up outside the catalogue. Played last, after a rule on the timeline.
  related: ["CustomerDetail", "PacketDetail", "ProjectDetail", "ProjectCostsForOne"],
  scenes: [
    // The catalogue's one create button, opened to show what it offers — a record of the catalogue or a
    // packet of it — and closed again with Escape. Neither entry is pressed: both leave the list for a
    // create form.
    { key: "create-menu", enter: [{ do: "click", target: "catalog-create" }], exit: [{ do: "escape" }] },
    // The Allgemein register and its three groups. One scene, so a jump straight onto the note presses
    // the register first, at speed. No exit: the next scene presses its own register, and leaving the
    // page takes the open one with it.
    // The actions menu of the card's head, opened to show its entries and closed with Escape. Every
    // entry leaves the page or deletes, so none is pressed.
    { key: "actions", enter: [{ do: "click", target: "entity-actions" }], exit: [{ do: "escape" }] },
    { key: "general", enter: [], exit: [] },
    // The Einkauf, once the prices are suppliers'. The quantity typed for the delivered prices is taken out
    // again by the field's ✕, so going back into the tab does not find the column already there.
    { key: "purchase", enter: [], exit: [{ do: "clear", target: "machine-purchase-quantity" }] },
    // The price history, folded until opened: its step opens it, the exit folds it again. The tab first, for a jump
    // straight onto the step.
    {
      key: "history",
      enter: [{ do: "openTab", target: "machine-tab-purchase" }],
      exit: [{ do: "collapse", target: "machine-purchase-history-toggle" }],
    },
    // The Dateiablage, and then the Protokoll. One press each, in a scene of its own, because a
    // scene's steps have to be consecutive.
    { key: "files", enter: [], exit: [] },
    { key: "activity", enter: [], exit: [] },
    // A customer's price register, and two of the project's, each pressed by the step that explains it.
    { key: "customer-machines", enter: [], exit: [] },
    { key: "project-machines", enter: [], exit: [] },
    // A report of the project opened from its register, left again by the form's own Abbrechen: the
    // drawer would otherwise stay open over the cost page's way back through the project.
    { key: "project-reports", enter: [], exit: [{ do: "click", target: "hwr-cancel" }] },
    // The Analyse view of the cost page, put back on the Heatmap the page opens on: the switch is a
    // toggle at the top of the page, not a tab of the register bar, and it survives a step's end.
    {
      key: "costs-analysis",
      enter: [{ do: "click", target: "costs-mode-analysis" }],
      exit: [{ do: "click", target: "costs-mode-heatmap" }],
    },
  ],
  steps: [
    // ── Übersicht ────────────────────────────────────────────────────────────
    onOverview("list", "machine-table", ENTITY_ICONS.machine, { placement: "top" }),
    onOverview("row", "row-mach-bagger", "mdi-table-row", { placement: "bottom" }),
    // On the open menu of the one create button, which offers the machine and the packet. Pointed at,
    // not pressed: pressing it would leave the list for the create form.
    onOverview("create", "machine-create", "mdi-plus", {
      placement: "bottom",
      scene: "create-menu",
      permissions: EDIT,
    }),
    // The catalogue's packets are rows of this table too, and the filter above it narrows to either.
    // Pointed at, not pressed: the packets tour explains the packets.
    onOverview("kind-filter", "catalog-kind-filter", ENTITY_ICONS.packet, {
      placement: "bottom",
      permissions: [PERMISSIONS.VIEW_PACKET_GENERAL],
    }),

    // ── Minibagger 3 t ───────────────────────────────────────────────────────
    // The card's head is `LewEntityPage`'s, not this view's, which is why it carries its own anchor.
    // A catalogue record is named by what the head shows, so it comes before the registers.
    onDetail("head", "entity-head", "mdi-card-account-details-outline", { placement: "bottom" }),
    // Duplizieren and Paket daraus erstellen, pointed at on the open menu: both open a form.
    onDetail("action-duplicate", "entity-action-duplicate", "mdi-content-duplicate", {
      placement: "left",
      scene: "actions",
      permissions: EDIT,
    }),
    onDetail("action-packet", "entity-action-packet", ENTITY_ICONS.packet, {
      placement: "left",
      scene: "actions",
      permissions: [PERMISSIONS.EDIT_PACKET_GENERAL],
    }),
    onDetail("tabs", "machine-tabs", "mdi-tab", { placement: "bottom" }),
    // The register is pressed with the whole page in shot: zoomed onto the tab, the panel that slides
    // in underneath would be out of the picture. The two groups after it are inside that panel.
    onDetail("base", "machine-base", "mdi-form-textbox", {
      placement: "bottom",
      scene: "general",
      zoom: false,
      actions: [{ do: "openTab", target: "machine-tab-general" }],
    }),
    // Before the setup only: afterwards the prices have a tab of their own, below.
    onDetail("purchase", "machine-purchase", "mdi-cash", {
      placement: "bottom",
      scene: "general",
      permissions: VIEW_PRICE,
      when: legacy,
    }),
    onDetail("note", "machine-note", "mdi-note-text-outline", { placement: "top", scene: "general" }),
    // The Einkauf: who sells the machine, at what price, since when. Read here, changed at the supplier.
    onDetail("purchase-tab", "machine-purchase", "mdi-truck-delivery-outline", {
      placement: "top",
      scene: "purchase",
      zoom: false,
      when: strict,
      actions: [{ do: "openTab", target: "machine-tab-purchase" }],
    }),
    // Brought on screen whole: reached straight from the timeline, the table starts under the footer.
    onDetail("purchase-rows", "machine-purchase-table", "mdi-table", {
      placement: "top",
      scene: "purchase",
      when: strict,
      actions: [{ do: "scroll", target: "machine-purchase-table", to: "reveal" }],
    }),
    // A row in another unit than the machine (Mietpark Nord's Minibagger per Woche): its price names it, and the
    // Abstand compares by the day or not at all.
    onDetail("purchase-unit", "machine-purchase-unit", "mdi-calendar-week", {
      placement: "top",
      scene: "purchase",
      when: strict,
      permissions: VIEW_PRICE,
    }),
    // The line under Mietpark Nord's row: its transport tariff in the class of the Minibagger's 3 t.
    onDetail("purchase-transport", "machine-purchase-transport", "mdi-tow-truck", {
      placement: "bottom",
      scene: "purchase",
      when: strict,
      permissions: VIEW_PRICE,
    }),
    // Under the same row: Mietpark Nord's Zubehör for the Minibagger (docs/suppliers.md, "Zubehör").
    onDetail("purchase-accessories", "machine-purchase-accessories", ITEM_KIND_ICONS.ACCESSORY, {
      placement: "bottom",
      scene: "purchase",
      when: strict,
    }),
    onDetail("purchase-default", "machine-purchase-default", "mdi-star-outline", {
      placement: "right",
      scene: "purchase",
      when: strict,
    }),
    // The pencil beside the default's price: pointed at, not pressed — the dialog writes a price.
    onDetail("purchase-change", "machine-purchase-change", "mdi-pencil-outline", {
      placement: "left",
      scene: "purchase",
      when: mayAddSupplier,
    }),
    // A quantity typed, with the whole table in view: the column it fills is the point.
    onDetail("purchase-quantity", "machine-purchase-table", "mdi-calculator-variant-outline", {
      placement: "top",
      scene: "purchase",
      zoom: false,
      when: strict,
      permissions: VIEW_PRICE,
      actions: [{ do: "type", target: "machine-purchase-quantity", text: "40" }],
    }),
    // Pointed at, not pressed: the dialog adds a row the moment it is confirmed.
    onDetail("purchase-add", "machine-purchase-add", "mdi-plus", {
      placement: "left",
      scene: "purchase",
      when: mayAddSupplier,
    }),
    onDetail("purchase-following", "following-bookings-hint", "mdi-calendar-sync-outline", {
      placement: "top",
      scene: "purchase",
      when: strict,
      permissions: REPRICE,
    }),
    // The add-on's chart, last of the Einkauf and in a scene of its own: folded on the tab, it is opened by its
    // head and folded again after, as the reader finds it. Its head says what the record costs in general.
    onDetail("purchase-history", "machine-purchase-history", "mdi-chart-line", {
      placement: "bottom",
      scene: "history",
      when: strict,
      navEntry: "nav-supplier-insights",
      permissions: VIEW_PRICE,
      actions: [{ do: "expand", target: "machine-purchase-history-toggle" }],
    }),
    onDetail("files", "machine-files", "mdi-folder-outline", {
      placement: "top",
      scene: "files",
      zoom: false,
      actions: [{ do: "openTab", target: "machine-tab-files" }],
    }),
    onDetail("activity", "machine-activity", "mdi-history", {
      placement: "top",
      scene: "activity",
      zoom: false,
      actions: [{ do: "openTab", target: "machine-tab-activity" }],
    }),
    // Explained, never pressed: it writes the record.
    onDetail("save", "machine-save", "mdi-content-save-outline", { placement: "top", permissions: EDIT }),

    // ── Where to find this again ─────────────────────────────────────────────
    onDetail("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom" }),

    // ── Vergleichen (add-on) ────────────────────────────────────────────────
    onComparison("comparison", "machine-comparison-cards", "mdi-compare-horizontal", { placement: "top" }),
    onComparison("comparison-pick", "machine-comparison-card-1", "mdi-swap-horizontal", { placement: "bottom" }),
    onComparison("comparison-only", "machine-comparison-only-0", "mdi-chevron-right", { placement: "top" }),

    // ── Zusammenhänge ──────────────────────────────────────────────────────────
    //
    // Not a tour of four more pages: what matters here is only where a machine takes effect outside
    // the catalogue. So each step names the place and what the machine does to it — and, where the
    // purchase price is what does the work, says which figure is computed from it.
    //
    // Both directions, in the order a machine travels: priced for a customer, bundled in a packet,
    // carried by a project, booked on one of its Stundenlohnarbeiten, and finally valued in that
    // project's running post-calculation.
    onCustomer("customer-price", "customer-machines", ENTITY_ICONS.customer, {
      placement: "bottom",
      scene: "customer-machines",
      zoom: false,
      actions: [{ do: "openTab", target: "customer-tab-machines" }],
    }),
    onPacket("packet-items", "packet-items", ENTITY_ICONS.packet, { placement: "top" }),
    onProject("project-machines", "project-machines", ENTITY_ICONS.project, {
      placement: "top",
      scene: "project-machines",
      zoom: false,
      actions: [{ do: "openTab", target: "project-tab-machines" }],
    }),
    // Where an hour of use is actually written down: the register is pressed, its oldest report opened —
    // hwr-nord-1, the first row, which books the Minibagger — and the form turned to its Maschinen
    // register. The register itself is the projects tour's to explain; this step shows only the place
    // in one report where a machine is entered. No zoom: the camera would chase three presses across
    // the page and the drawer.
    onProjectReports("project-report", "hwr-machines", ENTITY_ICONS.hourlywagereport, {
      placement: "left",
      scene: "project-reports",
      zoom: false,
      actions: [
        { do: "openTab", target: "project-tab-hourlyWageReports" },
        { do: "click", target: "row-hwr-nord-1" },
        { do: "openTab", target: "tab-tabMachines" },
      ],
    }),
    // And what those hours become. The Analyse view is switched on by the scene, so the step lands on
    // the Kostenstruktur with the share of Maschinen already drawn.
    onCosts("project-costs", "costs-analysis-structure", "mdi-chart-donut", {
      placement: "top",
      scene: "costs-analysis",
    }),
  ],
}
